import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CurrencyDollar, VideoCamera, Image as ImageIcon, TextAa, CheckCircle, Wallet, Globe, UsersThree, Hash } from "@phosphor-icons/react";
import FileUpload from "@/components/FileUpload";

const CATEGORY_OPTIONS = [
  "Comedy", "Music", "Dance", "Sports", "Food", "Beauty", "Lifestyle", "Education", "Gaming", "News", "Tech", "Other",
];

const HASHTAG_SUGGESTIONS = [
  "fyp", "viral", "trending", "funny", "music", "dance", "food", "love", "beauty", "life",
];

function extractHashtags(text) {
  if (!text) return [];
  const set = new Set();
  const matches = text.match(/#([A-Za-z0-9_]{1,40})/g) || [];
  matches.forEach((m) => set.add(m.slice(1).toLowerCase()));
  return Array.from(set);
}

export default function PostContent() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [contentType, setContentType] = useState("video");
  const [mediaUrl, setMediaUrl] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [videoUploadId, setVideoUploadId] = useState("");
  const [videoStatus, setVideoStatus] = useState("");
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState("");
  const [privacy, setPrivacy] = useState("public");
  const [draft, setDraft] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data));
  }, []);

  const tags = useMemo(() => extractHashtags(caption), [caption]);
  const suggestions = HASHTAG_SUGGESTIONS.filter((s) => !tags.includes(s)).slice(0, 6);

  const insertTag = (tag) => {
    setCaption((c) => {
      const sep = c && !c.endsWith(" ") ? " " : "";
      return `${c}${sep}#${tag}`;
    });
  };

  const createDraft = async () => {
    if (submitting) return;
    if (contentType === "text" && !caption.trim()) {
      toast.error("Enter text for your post");
      return;
    }
    if (contentType !== "text" && !mediaUrl.trim() && !videoUploadId) {
      toast.error("Upload a file first");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/posts", {
        content_type: contentType,
        media_url: contentType === "text" ? null : (mediaUrl.trim() || null),
        thumbnail_url: contentType === "video" ? (thumbnailUrl || null) : null,
        caption: caption.trim(),
        category: category || null,
        privacy,
        video_upload_id: contentType === "video" ? (videoUploadId || null) : null,
      });
      setDraft(data);
      toast.success("Draft created — pay to submit for approval.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create post");
    } finally {
      setSubmitting(false);
    }
  };

  const payAndSubmit = async () => {
    if (!draft) return;
    setSubmitting(true);
    try {
      await api.post(`/posts/${draft.post_id}/pay`);
      toast.success("Marked as submitted. Contact admin on WhatsApp to complete payment.");
      resetForm();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed");
    } finally {
      setSubmitting(false);
    }
  };

  const payFromBalance = async () => {
    if (!draft) return;
    setSubmitting(true);
    try {
      await api.post(`/posts/${draft.post_id}/pay-with-balance`);
      toast.success("Paid from balance. Awaiting admin approval.");
      resetForm();
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setDraft(null);
    setMediaUrl("");
    setThumbnailUrl("");
    setVideoUploadId("");
    setVideoStatus("");
    setCaption("");
    setCategory("");
    setPrivacy("public");
  };

  const fee = settings?.publish_fee ?? 0;
  const balance = user?.balance ?? 0;
  const canPayFromBalance = balance >= fee;

  return (
    <div className="p-6 md:p-8 max-w-2xl mx-auto space-y-6" data-testid="post-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-black">Create</h1>
        <p className="text-white/60 mt-1">
          Publish content and earn from every like, view and comment. A one-time fee applies to each post.
        </p>
      </div>

      {!draft ? (
        <Card className="bg-[#121212] border-white/10">
          <CardHeader>
            <CardTitle className="font-heading">New post</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <Tabs value={contentType} onValueChange={setContentType}>
              <TabsList className="grid grid-cols-3 bg-white/5">
                <TabsTrigger data-testid="tab-video" value="video" className="gap-2">
                  <VideoCamera size={16} weight="bold" /> Video
                </TabsTrigger>
                <TabsTrigger data-testid="tab-image" value="image" className="gap-2">
                  <ImageIcon size={16} weight="bold" /> Image
                </TabsTrigger>
                <TabsTrigger data-testid="tab-text" value="text" className="gap-2">
                  <TextAa size={16} weight="bold" /> Text
                </TabsTrigger>
              </TabsList>

              <TabsContent value="video" className="space-y-3 mt-4">
                <FileUpload
                  accept="video/*"
                  label="Video file"
                  value={mediaUrl}
                  onChange={(v) => { setMediaUrl(v); if (!v) setThumbnailUrl(""); }}
                  onUploaded={async (data) => {
                    // Both chunked-and-processing videos AND
                    // direct-to-Cloudinary videos take the same draft path:
                    // create a draft post immediately so the user can
                    // watch progress on their profile and hit
                    // "Ready to Edit" once processing completes.
                    if (data.chunked || data.cloudinary) {
                      try {
                        const { data: draft } = await api.post("/posts", {
                          content_type: "video",
                          media_url: null,
                          thumbnail_url: null,
                          caption: "",
                          category: null,
                          privacy: "public",
                          video_upload_id: data.upload_id,
                        });
                        toast.success(
                          data.cloudinary
                            ? "Uploaded! Ready to edit on your profile."
                            : "Upload done — processing in the background",
                        );
                        navigate(`/u/${user?.user_id}?draft=${draft.post_id}`);
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Failed to create draft");
                      }
                    } else {
                      setThumbnailUrl(data.thumbnail_path || "");
                      setVideoUploadId("");
                      setVideoStatus("ready");
                    }
                  }}
                  testId="video-upload"
                />
                {videoUploadId && videoStatus === "processing" && (
                  <div
                    className="rounded-lg border border-blue-500/40 bg-blue-500/10 p-3 text-sm text-blue-100"
                    data-testid="video-processing-badge"
                  >
                    <p className="font-heading font-bold flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
                      Video processing in background
                    </p>
                    <p className="text-blue-100/70 text-xs mt-1">
                      Your 1080p rendition is being prepared. You can create the post now — it'll go live automatically once ready.
                    </p>
                  </div>
                )}
              </TabsContent>
              <TabsContent value="image" className="space-y-3 mt-4">
                <FileUpload
                  accept="image/*"
                  label="Image file"
                  value={mediaUrl}
                  onChange={setMediaUrl}
                  testId="image-upload"
                />
              </TabsContent>
              <TabsContent value="text" className="mt-4">
                <p className="text-sm text-white/50">Your caption below will be shown as the full text post.</p>
              </TabsContent>
            </Tabs>

            {contentType !== "video" && (
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 font-bold">Caption</label>
                <Textarea
                  data-testid="caption-input"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Say something… use #hashtags to reach more people"
                  rows={4}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
                {suggestions.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="hashtag-suggestions">
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        data-testid={`hashtag-suggest-${s}`}
                        onClick={() => insertTag(s)}
                        className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-xs hover:bg-white/10 flex items-center gap-1"
                      >
                        <Hash size={12} weight="bold" /> {s}
                      </button>
                    ))}
                  </div>
                )}
                {tags.length > 0 && (
                  <p className="text-[11px] text-blue-300/80 mt-2">
                    {tags.map((t) => `#${t}`).join(" ")}
                  </p>
                )}
              </div>
            )}

            {contentType !== "video" && (
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 font-bold">Category</label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 mt-1" data-testid="category-picker">
                  {CATEGORY_OPTIONS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      data-testid={`category-${c}`}
                      onClick={() => setCategory(category === c ? "" : c)}
                      className={`px-3 py-2 rounded-md text-sm font-heading font-bold transition ${
                        category === c
                          ? "bg-gradient-to-r from-blue-500 to-emerald-500 text-white shadow-md shadow-blue-500/20"
                          : "bg-white/5 text-white/70 hover:bg-white/10"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {contentType !== "video" && (
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 font-bold">Privacy</label>
                <div className="grid grid-cols-2 gap-2 mt-1" data-testid="privacy-picker">
                  <PrivacyTile
                    active={privacy === "public"}
                    onClick={() => setPrivacy("public")}
                    icon={Globe}
                    label="Public"
                    desc="Anyone can view"
                    testId="privacy-public"
                  />
                  <PrivacyTile
                    active={privacy === "followers"}
                    onClick={() => setPrivacy("followers")}
                    icon={UsersThree}
                    label="Followers only"
                    desc="Only people who follow you"
                    testId="privacy-followers"
                  />
                </div>
              </div>
            )}

            {contentType === "video" ? (
              <div
                className="rounded-lg border border-blue-500/40 bg-blue-500/10 p-4 text-sm text-blue-100"
                data-testid="video-upload-hint"
              >
                <p className="font-heading font-bold flex items-center gap-2">
                  <VideoCamera size={16} weight="fill" /> Just pick a video
                </p>
                <p className="text-blue-100/80 text-xs mt-1">
                  Once your video finishes uploading we'll take you to your profile so you can
                  watch the progress. When it's ready, tap the thumbnail to edit trim, sound and
                  caption before paying to publish.
                </p>
              </div>
            ) : (
              <Button
                data-testid="post-content-btn"
                onClick={createDraft}
                disabled={submitting}
                className="w-full h-12 bg-gradient-to-r from-blue-500 via-fuchsia-500 to-emerald-500 hover:brightness-110 font-heading font-bold text-base"
              >
                Post Content
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-[#121212] border-white/10">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <CheckCircle size={22} weight="fill" className="text-emerald-400" />
              Draft ready
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-white/70 text-sm">
              Choose how to pay the publishing fee. Once payment is confirmed, admin will review and publish your post.
            </p>

            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
              <p className="text-xs uppercase tracking-widest font-bold text-blue-300">Publish fee</p>
              <p className="font-heading text-4xl font-black text-white mt-1">
                ${fee.toFixed(2)}
              </p>
              <p className="text-xs text-white/50 mt-1">
                Your balance: <span className="font-bold text-emerald-400">${balance.toFixed(2)}</span>
              </p>
            </div>

            <Button
              data-testid="pay-balance-btn"
              onClick={payFromBalance}
              disabled={submitting || !canPayFromBalance}
              className={`w-full h-14 font-heading font-bold text-lg gap-2 ${
                canPayFromBalance ? "bg-emerald-600 hover:bg-emerald-700" : "bg-white/5 text-white/40"
              }`}
            >
              <Wallet size={22} weight="fill" />
              {canPayFromBalance ? `Pay $${fee.toFixed(2)} from Balance` : `Need $${(fee - balance).toFixed(2)} more in balance`}
            </Button>

            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-xs uppercase tracking-widest text-white/40 font-bold">or</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <div className="p-3 rounded-lg bg-white/5 border border-white/10 text-xs text-white/70 text-center">
              Already paid outside the app? Click Submit and admin will confirm.
            </div>

            <Button
              data-testid="pay-publish-btn"
              onClick={payAndSubmit}
              disabled={submitting}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-heading font-bold gap-2"
            >
              <CurrencyDollar size={20} weight="bold" />
              Submit ${fee.toFixed(2)}
            </Button>

            <Button
              data-testid="pay-cancel-btn"
              onClick={() => setDraft(null)}
              variant="outline"
              className="w-full border-white/20 bg-transparent hover:bg-white/5"
            >
              Cancel
            </Button>
          </CardContent>
        </Card>
      )}

      {settings && (
        <Card className="bg-[#121212] border-white/10">
          <CardHeader>
            <CardTitle className="font-heading text-lg">How you earn</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-3 text-center">
            <RateCell label="per like" value={settings.like_reward} />
            <RateCell label="per view" value={settings.view_reward} />
            <RateCell label="per comment" value={settings.comment_reward} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function RateCell({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-white/50 font-bold">{label}</p>
      <p className="font-heading text-2xl font-black text-emerald-400">${value.toFixed(2)}</p>
    </div>
  );
}

function PrivacyTile({ active, onClick, icon: Icon, label, desc, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`p-3 rounded-lg text-left transition ${
        active
          ? "bg-blue-500/15 ring-1 ring-blue-400/60"
          : "bg-white/5 hover:bg-white/10 ring-1 ring-white/10"
      }`}
    >
      <div className="flex items-center gap-2">
        <Icon size={16} weight="bold" className={active ? "text-blue-300" : "text-white/60"} />
        <p className="font-heading font-bold text-sm">{label}</p>
      </div>
      <p className="text-[11px] text-white/50 mt-1">{desc}</p>
    </button>
  );
}
